import { useEffect, useState } from "react";

import moment from "moment";
import { useFormikContext } from "formik";
import { Button, Form, Offcanvas, Table } from "react-bootstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { InfoTooltip } from "features/assessment";
import { isArray, reorder } from "utils/ArrayUtils";
import RequiredAsterisk from "components/ui/RequiredAsterisk";
import { JSON_FIELD } from "features/assessment/constants/flexibleQuestionsFields";

import "./FieldJSONResponse.scss";

const isEqual = (a = "", b = "") => a.toString() === b.toString();

const MAX_ROWS = 300;
const MAX_ROWS_ON_FORM = 10;

const FieldJSONResponse = ({ question, value, isDisabled }) => {
  const [show, setShow] = useState(false);
  const [droppableId, setDroppableId] = useState("droppable");
  const { values, setFieldValue, handleBlur } = useFormikContext();

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const createEmptyResponse = () => ({
    id: `${Date.now()}`,
    data: question?.jsonStructure.map(({ id }) => ({
      id: `${id}`,
      value: "",
    })),
  });

  const formatDate = (value) => {
    return moment.utc(value, true).isValid() && isNaN(Number(value))
      ? moment.utc(value).format("YYYY-MM-DD")
      : value;
  };

  const updateResponses = () => {
    return value?.response.map((r) => {
      return {
        ...r,
        data: question?.jsonStructure.map((field) => {
          const found = r?.data.find(({ id }) => isEqual(id, field?.id));
          if (found) return found;
          return {
            id: `${field?.id}`,
            value: "",
          };
        }),
      };
    });
  };

  const responses =
    isArray(value?.response) && value?.response?.length
      ? updateResponses(value?.response)
      : [createEmptyResponse()];

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) return;

    setFieldValue(`dynamic.${question.id}`, {
      type: JSON_FIELD,
      response: reorder(
        responses,
        result.source.index,
        result.destination.index,
      ),
    });
  };

  useEffect(() => {
    setDroppableId("droppable-1");
  }, []);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Form.Text muted>
        Note: At least one field in each list item must be filled. Empty list
        items will be removed.
      </Form.Text>
      {show ? (
        <Table borderless responsive>
          <thead>
            <tr>
              <td></td>
              {question?.jsonStructure.map((field) => (
                <td key={field?.id}>
                  {field?.label} <InfoTooltip text={field?.description} />
                </td>
              ))}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {responses
              .filter((_, index) => index < MAX_ROWS_ON_FORM)
              .map(({ id, data = [] }, index) => (
                <tr key={id} index={index}>
                  <td>
                    <span
                      translate="no"
                      className="material-symbols-outlined md-16"
                    >
                      drag_indicator
                    </span>
                  </td>
                  {question?.jsonStructure.map((field) => {
                    const found =
                      data.find(({ id }) => isEqual(id, field?.id)) || {};

                    const fieldType =
                      field?.type === "datetime" || field?.type === "date"
                        ? "date"
                        : field?.type || "text";

                    return (
                      <td key={field?.id} className="px-3">
                        <Form.Control
                          size="sm"
                          name={field?.id}
                          type={fieldType}
                          {...(fieldType === "text" && {
                            maxLength: "255",
                          })}
                          placeholder={`Enter ${
                            field?.type || "text"
                          } answer here.`}
                          value={formatDate(found.value)}
                          onChange={(ev) => {
                            setFieldValue(`dynamic.${question.id}`, {
                              type: JSON_FIELD,
                              response: responses.map((r) => {
                                if (isEqual(r?.id, id)) {
                                  return {
                                    ...r,
                                    data: r.data.map((d) => {
                                      if (isEqual(d?.id, field?.id)) {
                                        return {
                                          ...d,
                                          value: ev.target.value,
                                        };
                                      }
                                      return d;
                                    }),
                                  };
                                }
                                return r;
                              }),
                            });
                          }}
                          onBlur={handleBlur}
                          disabled={values.isInapplicable || isDisabled}
                        />
                      </td>
                    );
                  })}
                  <td>
                    <Button
                      size="sm"
                      variant="light"
                      disabled={values.isInapplicable || isDisabled}
                      onClick={() => {
                        const updated = responses.filter(
                          (r) => !isEqual(r.id, id),
                        );
                        setFieldValue(`dynamic.${question.id}`, {
                          type: JSON_FIELD,
                          response: updated?.length
                            ? updated
                            : [createEmptyResponse()],
                        });
                      }}
                    >
                      <span
                        translate="no"
                        className="material-symbols-outlined md-14 text-danger"
                      >
                        delete
                      </span>
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      ) : (
        <Droppable key={droppableId} droppableId={droppableId} className="JSON">
          {(provided, snapshot) => (
            <Table
              borderless
              responsive
              ref={provided.innerRef}
              className={`JSON ${
                snapshot.isDraggingOver ? "bg-light bg-opacity-75" : ""
              }`}
            >
              <thead>
                <tr>
                  <td></td>
                  {question?.jsonStructure.map((field) => (
                    <td key={field?.id}>
                      {field?.label} <InfoTooltip text={field?.description} />
                    </td>
                  ))}
                  <td></td>
                </tr>
              </thead>
              <tbody ref={provided.innerRef} {...provided.droppableProps}>
                {responses
                  .filter((_, index) => index < MAX_ROWS_ON_FORM)
                  .map(({ id, data = [] }, index) => (
                    <Draggable
                      key={id}
                      index={index}
                      draggableId={id}
                      isDragDisabled={values.isInapplicable || isDisabled}
                    >
                      {(provided) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={
                            snapshot.isDragging ? "bg-primary" : "bg-white"
                          }
                        >
                          <td>
                            <span
                              translate="no"
                              className="material-symbols-outlined md-16"
                            >
                              drag_indicator
                            </span>
                          </td>
                          {question?.jsonStructure.map((field) => {
                            const found =
                              data.find(({ id }) => isEqual(id, field?.id)) ||
                              {};

                            const fieldType =
                              field?.type === "datetime" ||
                              field?.type === "date"
                                ? "date"
                                : field?.type || "text";

                            return (
                              <td key={field?.id} className="px-3">
                                <Form.Control
                                  size="sm"
                                  name={field?.id}
                                  type={fieldType}
                                  {...(fieldType === "text" && {
                                    maxLength: "255",
                                  })}
                                  placeholder={`Enter ${
                                    field?.type || "text"
                                  } answer here.`}
                                  value={formatDate(found.value)}
                                  onChange={(ev) => {
                                    setFieldValue(`dynamic.${question.id}`, {
                                      type: JSON_FIELD,
                                      response: responses.map((r) => {
                                        if (isEqual(r?.id, id)) {
                                          return {
                                            ...r,
                                            data: r.data.map((d) => {
                                              if (isEqual(d?.id, field?.id)) {
                                                return {
                                                  ...d,
                                                  value: ev.target.value,
                                                };
                                              }
                                              return d;
                                            }),
                                          };
                                        }
                                        return r;
                                      }),
                                    });
                                  }}
                                  onBlur={handleBlur}
                                  disabled={values.isInapplicable || isDisabled}
                                />
                              </td>
                            );
                          })}
                          <td>
                            <Button
                              size="sm"
                              variant="light"
                              disabled={values.isInapplicable || isDisabled}
                              onClick={() => {
                                const updated = responses.filter(
                                  (r) => !isEqual(r.id, id),
                                );
                                setFieldValue(`dynamic.${question.id}`, {
                                  type: JSON_FIELD,
                                  response: updated?.length
                                    ? updated
                                    : [createEmptyResponse()],
                                });
                              }}
                            >
                              <span
                                translate="no"
                                className="material-symbols-outlined md-14 text-danger"
                              >
                                delete
                              </span>
                            </Button>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </tbody>
            </Table>
          )}
        </Droppable>
      )}

      <Offcanvas
        scroll
        show={show}
        placement="end"
        onHide={handleClose}
        className="w-50"
      >
        <Offcanvas.Header className="border-bottom" closeButton>
          <div>
            <Offcanvas.Title>
              {question?.prompt}{" "}
              <InfoTooltip id={question?.id} text={question?.description} />
            </Offcanvas.Title>
            <Form.Text muted>
              Note: At least one field in each list item must be filled. Empty
              list items will be removed.
            </Form.Text>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className="pb-5">
          <Droppable
            key={droppableId}
            droppableId={droppableId}
            className="JSON"
          >
            {(provided, snapshot) => (
              <Table
                borderless
                responsive
                ref={provided.innerRef}
                className={`JSON ${
                  snapshot.isDraggingOver ? "bg-light bg-opacity-75" : ""
                }`}
              >
                <thead>
                  <tr>
                    <td></td>
                    <td></td>
                    {question?.jsonStructure.map((field) => (
                      <td key={field?.id}>
                        {field?.label} <InfoTooltip text={field?.description} />
                      </td>
                    ))}
                    <td></td>
                  </tr>
                </thead>
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {responses.map(({ id, data = [] }, index) => (
                    <Draggable
                      key={id}
                      index={index}
                      draggableId={id}
                      isDragDisabled={values.isInapplicable || isDisabled}
                    >
                      {(provided) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={
                            snapshot.isDragging ? "bg-primary" : "bg-white"
                          }
                        >
                          <td>
                            {" "}
                            <small className="text-muted pt-1">
                              {index + 1}
                            </small>
                          </td>
                          <td>
                            <span
                              translate="no"
                              className="material-symbols-outlined md-16"
                            >
                              drag_indicator
                            </span>
                          </td>
                          {question?.jsonStructure.map((field) => {
                            const found =
                              data.find(({ id }) => isEqual(id, field?.id)) ||
                              {};

                            const fieldType =
                              field?.type === "datetime" ||
                              field?.type === "date"
                                ? "date"
                                : field?.type || "text";

                            return (
                              <td key={field?.id} className="px-3">
                                <Form.Control
                                  size="sm"
                                  name={field?.id}
                                  type={fieldType}
                                  {...(fieldType === "text" && {
                                    maxLength: "255",
                                  })}
                                  placeholder={`Enter ${
                                    field?.type || "text"
                                  } answer here.`}
                                  value={formatDate(found.value)}
                                  onChange={(ev) => {
                                    setFieldValue(`dynamic.${question.id}`, {
                                      type: JSON_FIELD,
                                      response: responses.map((r) => {
                                        if (isEqual(r?.id, id)) {
                                          return {
                                            ...r,
                                            data: r.data.map((d) => {
                                              if (isEqual(d?.id, field?.id)) {
                                                return {
                                                  ...d,
                                                  value: ev.target.value,
                                                };
                                              }
                                              return d;
                                            }),
                                          };
                                        }
                                        return r;
                                      }),
                                    });
                                  }}
                                  onBlur={handleBlur}
                                  disabled={values.isInapplicable || isDisabled}
                                />
                              </td>
                            );
                          })}
                          <td>
                            <Button
                              size="sm"
                              variant="light"
                              disabled={values.isInapplicable || isDisabled}
                              onClick={() => {
                                const updated = responses.filter(
                                  (r) => !isEqual(r.id, id),
                                );
                                setFieldValue(`dynamic.${question.id}`, {
                                  type: JSON_FIELD,
                                  response: updated?.length
                                    ? updated
                                    : [createEmptyResponse()],
                                });
                              }}
                            >
                              <span
                                translate="no"
                                className="material-symbols-outlined md-14 text-danger"
                              >
                                delete
                              </span>
                            </Button>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </Table>
            )}
          </Droppable>
        </Offcanvas.Body>
        <div className="d-flex flex-row p-3 bg-light position-absolute bottom-0  w-100">
          <div>
            {responses.length < MAX_ROWS ? (
              <Button
                size="sm"
                variant="light"
                className="border ms-2"
                disabled={
                  isDisabled ||
                  values.isInapplicable ||
                  responses.length >= MAX_ROWS
                }
                onClick={() => {
                  setFieldValue(`dynamic.${question.id}`, {
                    type: JSON_FIELD,
                    response: [...responses, createEmptyResponse()],
                  });
                }}
              >
                <span
                  translate="no"
                  className="material-symbols-outlined md-16"
                >
                  add
                </span>
                Add List Item
              </Button>
            ) : (
              <small className="ms-3 text-muted">
                Maximum {MAX_ROWS} list items allowed.
              </small>
            )}
          </div>
          <div className="flex-fill">
            <span className="float-end">
              Showing <strong>{responses?.length}</strong> Items
            </span>
          </div>
        </div>
      </Offcanvas>
      <div className="d-flex flex-row">
        <div>
          {responses?.length >= MAX_ROWS_ON_FORM ? (
            <Button
              size="sm"
              variant="light"
              className="border ms-2"
              onClick={handleShow}
            >
              <span translate="no" className="material-symbols-outlined md-16">
                expand_content
              </span>{" "}
              Expand
            </Button>
          ) : (
            <Button
              size="sm"
              variant="light"
              className="border ms-2"
              disabled={
                isDisabled ||
                values.isInapplicable ||
                responses.length >= MAX_ROWS
              }
              onClick={() => {
                setFieldValue(`dynamic.${question.id}`, {
                  type: JSON_FIELD,
                  response: [...responses, createEmptyResponse()],
                });
              }}
            >
              <span translate="no" className="material-symbols-outlined md-16">
                add
              </span>
              Add List Item
            </Button>
          )}
          <br />
          <small className="text-muted">
            <RequiredAsterisk />
            Maximum {MAX_ROWS} list items allowed.
          </small>
        </div>
        <div className="flex-fill">
          <span className="float-end">
            {responses?.length > MAX_ROWS_ON_FORM ? (
              <>
                Showing <strong>{MAX_ROWS_ON_FORM}</strong> of{" "}
                <strong>{responses?.length}</strong>{" "}
                {responses?.length === 1 ? "Item" : "Items"}
              </>
            ) : (
              <>
                Showing <strong>{responses?.length}</strong>{" "}
                {responses?.length === 1 ? "Item" : "Items"}
              </>
            )}
          </span>
        </div>
      </div>
    </DragDropContext>
  );
};

export default FieldJSONResponse;
